import { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowUpRight, ArrowDownRight, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';

interface KPICardProps {
  title: string;
  value: number;
  change?: number;
  changeLabel?: string;
  prefix?: string;
  suffix?: string;
  icon: React.ReactNode;
  iconBgColor?: string;
  iconColor?: string;
  format?: 'number' | 'currency' | 'percentage';
  isLoading?: boolean;
  delay?: number;
}

function useCountUp(end: number, duration: number = 1500, start: boolean = true) {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const startTimeRef = useRef<number | null>(null);

  useEffect(() => {
    if (!start) {
      setCount(0);
      return;
    }

    startTimeRef.current = null;
    countRef.current = 0;

    const animate = (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const progress = Math.min((timestamp - startTimeRef.current) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      
      const currentCount = Math.floor(easeOutQuart * end);
      countRef.current = currentCount;
      setCount(currentCount);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, start]);

  return count;
}

export function KPICard({
  title,
  value,
  change,
  changeLabel = 'vs last period',
  prefix = '',
  suffix = '',
  icon,
  iconBgColor = 'bg-[#2B7FFF]/10',
  iconColor = 'text-[#2B7FFF]',
  format = 'number',
  isLoading = false,
  delay = 0,
}: KPICardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const animatedValue = useCountUp(value, 1500, isVisible);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  const formatValue = (val: number) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          notation: val >= 1000000 ? 'compact' : 'standard',
          maximumFractionDigits: val >= 1000000 ? 1 : 0,
        }).format(val);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return new Intl.NumberFormat('en-US', {
          notation: val >= 1000000 ? 'compact' : 'standard',
          maximumFractionDigits: 0,
        }).format(val);
    }
  };

  const isPositiveChange = change && change > 0;
  const isNegativeChange = change && change < 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: delay / 1000, ease: [0.4, 0, 0.2, 1] }}
    >
      <Card className="relative overflow-hidden group transition-shadow duration-200 hover:shadow-md cursor-pointer">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{title}</p>
              <div className="flex items-baseline gap-1 mt-2">
                {prefix && <span className="text-2xl font-bold text-foreground">{prefix}</span>}
                <h3 className="text-2xl font-semibold tracking-tight text-foreground">
                  {isLoading ? (
                    <span className="inline-block w-24 h-8 bg-muted rounded animate-pulse" />
                  ) : (
                    formatValue(animatedValue)
                  )}
                </h3>
                {suffix && <span className="text-base font-semibold text-muted-foreground">{suffix}</span>}
              </div>
              
              {change !== undefined && (
                <div className="flex items-center gap-1.5 mt-3">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: (delay + 500) / 1000, type: 'spring', stiffness: 500 }}
                    className={cn(
                      'flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium',
                      isPositiveChange 
                        ? 'bg-green-500/10 text-green-600 dark:text-green-400' 
                        : isNegativeChange
                        ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {isPositiveChange ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : isNegativeChange ? (
                      <TrendingDown className="h-3 w-3" />
                    ) : null}
                    {isPositiveChange && <ArrowUpRight className="h-3 w-3" />}
                    {isNegativeChange && <ArrowDownRight className="h-3 w-3" />}
                    <span>{Math.abs(Number(change) || 0).toFixed(1)}%</span>
                  </motion.div>
                  <span className="text-xs text-muted-foreground">{changeLabel}</span>
                </div>
              )}
            </div>
            
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: (delay + 120) / 1000, duration: 0.2 }}
              className={cn(
                'h-11 w-11 rounded-2xl flex items-center justify-center transition-colors',
                iconBgColor
              )}
            >
              <div className={cn('h-6 w-6', iconColor)}>{icon}</div>
            </motion.div>
          </div>
          
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-primary/5 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        </CardContent>
      </Card>
    </motion.div>
  );
}

// Mini KPI Card for smaller spaces
interface MiniKPICardProps {
  title: string;
  value: number;
  change?: number;
  format?: 'number' | 'currency' | 'percentage';
  isLoading?: boolean;
}

export function MiniKPICard({
  title,
  value,
  change,
  format = 'number',
  isLoading = false,
}: MiniKPICardProps) {
  const [isVisible, setIsVisible] = useState(false);
  const animatedValue = useCountUp(value, 1200, isVisible);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const formatValue = (val: number) => {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          notation: 'compact',
          maximumFractionDigits: 1,
        }).format(val);
      case 'percentage':
        return `${val.toFixed(1)}%`;
      default:
        return new Intl.NumberFormat('en-US', { notation: 'compact' }).format(val);
    }
  };

  return (
    <div className="flex flex-col">
      <span className="text-xs text-muted-foreground uppercase tracking-wider">{title}</span>
      <div className="flex items-baseline gap-2 mt-1">
        <span className="text-xl font-bold text-foreground">
          {isLoading ? (
            <span className="inline-block w-16 h-6 bg-muted rounded animate-pulse" />
          ) : (
            formatValue(animatedValue)
          )}
        </span>
        {change !== undefined && (
          <span
            className={cn(
              'text-xs font-medium',
              change > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            )}
          >
            {change > 0 ? '+' : ''}{change.toFixed(1)}%
          </span>
        )}
      </div>
    </div>
  );
}

export default KPICard;
